import { createSlice } from '@reduxjs/toolkit'
import { getPrecision, plus, times, divide, minus } from 'utils/math.utils'
import { fetchUserInformation } from 'redux-thunk/thunk/user.thunk'
import { userLogin } from 'redux-thunk/thunk/auth.thunk'
import { getCategories, getMarkets, getMarketDetails, getOptionDetails, getOptionGraphData, getMyPositions, getMyOrders, getMyOptionOrders, getActiveMarketOrder,
  getMarketComments, getMyMarketComments, getTopHolders, addComment, deleteComment, getMyOptionPosition,
  getOptionOrderbook, getHistory, getMyProfit, getMarketActivity, getTopPositions, toggleFavouriteQuestion, loadNotifications, getNews, cancelLimitOrder, likeToggle } from './predictionMarket.thunk'
import { appendLivePriceToOptionDataPoints, getPortfolioOrderSliceFields, mapActivityLogRow, mapMarketActivityLogRow, mapActivityToNotification, mapWalletToNotification, prependNotification, updateMyActiveMarketOrder, updateMyLimitOrders, updateMyOptionOrders, updateOrderbook, mapOptionOrderRow } from 'games/PredictionMarket/predictionMarket.helpers'
import { ORDER_SIDES, ORDER_STATES, ORDER_TYPES, OUTCOME_TYPES, MARKET_OPTION_EMIT_TYPES, USER_ACTIVITY_TYPES, USER_POSITION_STATES } from 'games/PredictionMarket/predictionMarket.constants'
// import { current } from 'immer'

const initialState = {
  marketsLoading: false,
  marketDetailLoading: false,
  marketDetailRequestId: null,
  componentLoader: {
    myPositions: false,
    myOrders: false,
    history: false,
    marketActivity: false,
    portfolio: false,
    marketComments: false,
    myMarketComments: false,
    topYesHolders: false,
    topNoHolders: false,
    topPositions: false,
    myOptionPosition: false,
    myLimitOrders: false,
    myOptionOrders: false,
    optionOrderbook: false
  },
  categories: [],
  categoryTree: [],
  activeSubFilter: null,
  markets: { rows: [], count: 0 },
  chosenMarket: null,
  portfolioData: null,
  myPositions: { page: 1, rows: [], count: 0 },
  myOptionPosition: null,
  myLimitOrders: [],
  myOptionOrders: {
    page: 1,
    rows: [],
    count: 0,
    state: ORDER_STATES.OPEN,
    orderType: ORDER_TYPES.LIMIT,
    side: ORDER_SIDES.BID
  },
  activeMarketOrder: null,
  myOrders: { page: 1, rows: [], count: 0 },
  history: { page: 1, rows: [], count: 0 },
  topYesHolders: { page: 1, rows: [], count: 0 },
  topNoHolders: { page: 1, rows: [], count: 0 },
  topPositions: { page: 1, rows: [], count: 0 },
  myMarketComments: { page: 1, rows: [], count: 0 },
  marketComments: { page: 1, rows: [], count: 0 },
  marketActivity: { page: 1, rows: [], count: 0 },
  favouriteQuestionIds: [],
  notifications: {
    items: [],
    unreadCount: 0,
    isOpen: false,
    loading: false
  },
  newsInfo: {
    isOpen: false,
    loading: false,
    topCount: 10,
    byCategory: {}
  },
  isDemoMode: false,
  isDemoMarketClosed: false,
  isDemoMarketSettled: false,
  demoOrders: [],
  demoWallet: null
}

const mapPortfolioOrderRow = (order) => {
  const shared = {
    thumbnailUrl: order.thumbnailUrl,
    title: order.questionTitle,
    option: order.optionTitle,
    side: order.side,
    outcome: order.outcome,
    state: order.state
  }
  const meta = {
    id: order.id,
    questionId: order.questionId,
    optionId: order.optionId
  }


  return {
    ...shared,
    ...getPortfolioOrderSliceFields(order),
    ...meta
  }
}

const predictionMarketSlice = createSlice({
  name: 'predictionMarket',
  initialState,
  reducers: {
    setActiveCategory: (state, action) => {
      return {
        ...state,
        activeCategory: action.payload
      }
    },
    toggleNewsPanel: (state) => {
      return {
        ...state,
        newsInfo: {
          ...state.newsInfo,
          isOpen: !state.newsInfo.isOpen
        }
      }
    },
    closeNewsPanel: (state) => {
      if (!state.newsInfo.isOpen) return state
      return {
        ...state,
        newsInfo: {
          ...state.newsInfo,
          isOpen: false
        }
      }
    },
    setChosenMarket: (state, action) => {
      return {
        ...state,
        chosenMarket: action.payload
      }
    },
    setIsDemoMode: (state, action) => ({
      ...state,
      isDemoMode: Boolean(action.payload),
      demoOrders: action.payload ? state.demoOrders : [],
      isDemoMarketClosed: action.payload ? false : state.isDemoMarketClosed,
      isDemoMarketSettled: action.payload ? false : state.isDemoMarketSettled,
      demoWallet: action.payload ? state.demoWallet : null
    }),
    setDemoWallet: (state, action) => ({
      ...state,
      demoWallet: action.payload
        ? {
            balance: action.payload.balance ?? 0,
            currency: action.payload.currency ?? 'USD'
          }
        : null
    }),
    demoOrdersReset: (state, action) => ({
      ...state,
      demoOrders: (action.payload ?? []).map((order) => ({ ...order }))
    }),
    demoOrdersAdd: (state, action) => {
      const order = action.payload
      if (!order) return state
      return { ...state, demoOrders: [...state.demoOrders, { ...order }] }
    },
    demoOrdersRemove: (state, action) => ({
      ...state,
      demoOrders: state.demoOrders.filter((o) => +o.id !== +action.payload?.orderId)
    }),
    demoSyncBook: (state, action) => {
      const { orderbook, myLimitOrders, yesPrice, optionId } = action.payload ?? {}
      if (!orderbook && !myLimitOrders) return state
      const patchOptionPrices = (options) => (options ?? []).map((option) =>
        yesPrice != null && optionId != null && +option.id === +optionId
          ? { ...option, yesPrice, noPrice: 100 - yesPrice }
          : option
      )
      return {
        ...state,
        ...(myLimitOrders ? { myLimitOrders } : {}),
        ...(orderbook && state.chosenMarket ? {
          chosenMarket: {
            ...state.chosenMarket,
            orderbook,
            ...(yesPrice != null && optionId != null ? {
              options: patchOptionPrices(state.chosenMarket.options)
            } : {})
          }
        } : {})
      }
    },
    demoMarketClosed: (state, action) => {
      const { userOrders, closedOptions } = action.payload ?? {}
      const closedMap = Object.fromEntries(
        (closedOptions ?? []).map((option) => [+option.id, option])
      )

      const patchOptions = (options) => (options ?? []).map((option) => {
        const closed = closedMap[+option.id]
        if (!closed) return option
        return { ...option, state: closed.state ?? 'closed', outcome: null }
      })

      if (!state.chosenMarket) {
        return {
          ...state,
          isDemoMarketClosed: true,
          demoOrders: (userOrders ?? []).map((order) => ({ ...order })),
          myLimitOrders: []
        }
      }

      return {
        ...state,
        isDemoMarketClosed: true,
        demoOrders: (userOrders ?? []).map((order) => ({ ...order })),
        myLimitOrders: [],
        chosenMarket: {
          ...state.chosenMarket,
          orderbook: { yes: { bid: {}, ask: {} }, no: { bid: {}, ask: {} } },
          options: patchOptions(state.chosenMarket.options)
        }
      }
    },
    demoMarketSettled: (state, action) => {
      const { settledOptions } = action.payload ?? {}
      const settledMap = Object.fromEntries(
        (settledOptions ?? []).map((option) => [+option.id, option])
      )

      const patchOptions = (options) => (options ?? []).map((option) => {
        const settled = settledMap[+option.id]
        if (!settled) return option
        return {
          ...option,
          state: settled.state ?? 'settled',
          outcome: settled.outcome ?? OUTCOME_TYPES.YES
        }
      })

      if (!state.chosenMarket) {
        return { ...state, isDemoMarketSettled: true }
      }

      const options = patchOptions(state.chosenMarket.options)
      return {
        ...state,
        isDemoMarketSettled: options.every((option) => option.state === 'settled'),
        chosenMarket: { ...state.chosenMarket, options }
      }
    },
    setChosenOptionId: (state, action) => {
      if (!state.chosenMarket) return state
      if (
        state.chosenMarket.chosenOptionId != null
        && String(state.chosenMarket.chosenOptionId) === String(action.payload)
      ) {
        return state
      }

      return {
        ...state,
        myLimitOrders: [],
        myOptionPosition: null,
        myOptionOrders: {
          page: 1,
          rows: [],
          count: 0,
          state: ORDER_STATES.OPEN,
          orderType: ORDER_TYPES.LIMIT,
          side: ORDER_SIDES.BID
        },
        chosenMarket: {
          ...state.chosenMarket,
          chosenOptionId: action.payload
        }
      }
    },
    setOrder: (state, action) => {
      const { id, orderbook } = action.payload

      if (!state.chosenMarket) return state
    
      return {
        ...state,
        chosenMarket: {
          ...state.chosenMarket,
          options: state.chosenMarket.options.map(option =>
            option.id === id
              ? { ...option, orderbook }
              : option
          )
        }
      }
    },
    commentAdded: (state, action) => {
      if (action.payload.parentCommentId !== null) {
        return {
          ...state,
          marketComments: {
            ...state.marketComments,
            rows: state.marketComments.rows.map(comment => {
              if (+comment.id === +action.payload.parentCommentId) {
                return {
                  ...comment,
                  replies: [{ ...action.payload, likedByMe: false }, ...(comment.replies || [])],
                  repliesCount: (comment.repliesCount || 0) + 1
                }
              }
              return comment
            })
          }
        }
      }
      return {
        ...state,
        marketComments: {
          rows: [{ ...action.payload, replies: [], repliesCount: 0, likedByMe: false }, ...state.marketComments.rows],
          count: state.marketComments.count + 1
        }
      }
    },
    commentDeleted: (state, action) => {
      const { commentId, parentCommentId } = action.payload

      if (parentCommentId != null) {
        return {
          ...state,
          marketComments: {
            ...state.marketComments,
            rows: state.marketComments.rows.map(comment => {
              if (+comment.id !== +parentCommentId) return comment
              return {
                ...comment,
                replies: (comment.replies || []).filter(reply => +reply.id !== +commentId),
                repliesCount: Math.max(0, (comment.repliesCount || 0) - 1)
              }
            })
          }
        }
      }

      return {
        ...state,
        marketComments: {
          rows: state.marketComments.rows.filter(comment => +comment.id !== +commentId),
          count: Math.max(0, state.marketComments.count - 1)
        }
      }
    },
    userPositionUpdated: (state, action) => {
      const userPosition = action.payload
      const chosenOptionId = state.chosenMarket?.chosenOptionId

      if (chosenOptionId == null || +userPosition.optionId !== +chosenOptionId) {
        return state
      }

      return {
        ...state,
        myOptionPosition: userPosition
      }
    },
    userActivityReceived: (state, action) => {
      const activityRow = mapActivityLogRow(action.payload)
      const notification = mapActivityToNotification(action.payload)
      const previousLength = state.notifications.items.length
      const items = prependNotification(state.notifications.items, notification)
      const unreadIncrement = !state.notifications.isOpen && items.length > previousLength ? 1 : 0
      const chosenOptionId = state.chosenMarket?.chosenOptionId
      const myUserId = action.payload?.myUserId ?? action.payload?.userId

      return {
        ...state,
        activeMarketOrder: updateMyActiveMarketOrder(state.activeMarketOrder, action.payload),
        myOptionOrders: updateMyOptionOrders(
          state.myOptionOrders,
          action.payload,
          chosenOptionId,
          myUserId
        ),
        history: {
          ...state.history,
          rows: [activityRow, ...state.history.rows],
        },
        notifications: {
          ...state.notifications,
          items,
          unreadCount: state.notifications.unreadCount + unreadIncrement
        }
      }
    },
    marketActivityReceived: (state, action) => {
      const relevantOptionId = action.payload?.optionId

      const activityRow = mapMarketActivityLogRow(action.payload)

      const shouldUpdateOrderbook = relevantOptionId && state.chosenMarket?.orderbook
      && +action.payload.optionId === +state.chosenMarket.chosenOptionId
      
      const trade = action.payload?.log?.trade
      const myUserId = action.payload?.myUserId
      const dataPoint = action.payload?.type === USER_ACTIVITY_TYPES.BOUGHT_SHARES && trade ? {
        price: trade.graphPrice != null
          ? +trade.graphPrice
          : (trade.outcome === OUTCOME_TYPES.YES ? +trade.price : +minus(100, trade.price)),
        timestamp: new Date(trade.createdAt).getTime()
      } : null

      // Market feed is shared — only mutate myLimitOrders for the currently viewed option
      const shouldUpdateMyLimitOrders = relevantOptionId
        && +action.payload.optionId === +state.chosenMarket?.chosenOptionId
        && !state.isDemoMode
      const updatedMyLimitOrders = shouldUpdateMyLimitOrders
        ? updateMyLimitOrders(state.myLimitOrders, action.payload, myUserId)
        : state.myLimitOrders

      return {
        ...state,
        marketActivity: {
          ...state.marketActivity,
          rows: [activityRow, ...state.marketActivity.rows]
        },
        ...(relevantOptionId && {
          ...(shouldUpdateMyLimitOrders ? { myLimitOrders: updatedMyLimitOrders } : {}),
          chosenMarket: {
            ...state.chosenMarket,
            ...(shouldUpdateOrderbook && !state.isDemoMode ? { orderbook: updateOrderbook(state.chosenMarket.orderbook, action.payload)} : {}),
            options: state.chosenMarket.options.map(option => +option.id === +relevantOptionId ? {
              ...option,
              yesPrice: dataPoint ? dataPoint.price : option.yesPrice,
              noPrice: dataPoint ? +(100 - dataPoint.price) : option.noPrice,
              optionDataPoints: appendLivePriceToOptionDataPoints(option.optionDataPoints, dataPoint)
            }: option),
          }
        })
      }
    },
    marketOptionReceived: (state, action) => {
      const { type, questionId, optionId, state: optionState, outcome, noMatchReason, options: incoming, priceOutcome } = action.payload || {}
      if (!state.chosenMarket) return state
      if (questionId != null && +state.chosenMarket.id !== +questionId) return state

      if (type === MARKET_OPTION_EMIT_TYPES.OPTION_STATE_CHANGED) {
        if (!optionId || !state.chosenMarket.options) return state

        return {
          ...state,
          chosenMarket: {
            ...state.chosenMarket,
            options: state.chosenMarket.options.map((option) =>
              +option.id === +optionId
                ? {
                    ...option,
                    state: optionState,
                    outcome: outcome ?? null,
                    extraData: {
                      ...(option.extraData || {}),
                      ...(noMatchReason ? { noMatch: true, noMatchReason, priceOutcome } : {})
                    }
                  }
                : option
            ),
          }
        }
      }

      if (type === MARKET_OPTION_EMIT_TYPES.OPTIONS_CREATED) {
        const created = Array.isArray(incoming) ? incoming : []
        if (!created.length) return state

        const existingIds = new Set((state.chosenMarket.options || []).map((o) => +o.id))
        const newOptions = created.filter((o) => !existingIds.has(+o.id))
        if (!newOptions.length) return state

        return {
          ...state,
          chosenMarket: {
            ...state.chosenMarket,
            options: [...(state.chosenMarket.options || []), ...newOptions]
          }
        }
      }

      return state
    },
    toggleNotificationsPanel: (state) => {
      const isOpen = !state.notifications.isOpen
      return {
        ...state,
        notifications: {
          ...state.notifications,
          isOpen,
          ...(isOpen ? {
            items: state.notifications.items.map((item) => ({ ...item, read: true })),
            unreadCount: 0
          } : {})
        }
      }
    },
    closeNotificationsPanel: (state) => {
      return {
        ...state,
        notifications: {
          ...state.notifications,
          isOpen: false
        }
      }
    },
    markAllNotificationsRead: (state) => {
      return {
        ...state,
        notifications: {
          ...state.notifications,
          items: state.notifications.items.map((item) => ({ ...item, read: true })),
          unreadCount: 0
        }
      }
    },
    walletBalanceNotificationReceived: (state, action) => {
      const notification = mapWalletToNotification(action.payload)
      return {
        ...state,
        notifications: {
          ...state.notifications,
          items: prependNotification(state.notifications.items, notification),
          unreadCount: state.notifications.isOpen
            ? state.notifications.unreadCount
            : state.notifications.unreadCount + 1
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategories.fulfilled, (state, action) => {
        const payload = action.payload
        if (payload && !Array.isArray(payload)) {
          return {
            ...state,
            categories: payload.categories || [],
            categoryTree: payload.tree || []
          }
        }
        return {
          ...state,
          categories: payload || []
        }
      })
    builder
      .addCase(getMarkets.pending, (state, action) => {
        return {
          ...state,
          marketsLoading: true
        }
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        return {
          ...state,
          markets: { rows: action.payload.markets, count: action.payload.count },
          subFilters: action.payload.subFilters,
          marketsLoading: false
        }
      })
      .addCase(getMarkets.rejected, (state, action) => {
        return {
          ...state,
          marketsLoading: false
        }
      })
    builder
      .addCase(getMarketDetails.pending, (state, action) => {
        if (action.meta.arg?.background) return state
        return {
          ...state,
          marketDetailLoading: true,
          marketDetailRequestId: action.meta.requestId
        }
      })
      .addCase(getMarketDetails.fulfilled, (state, action) => {
        if (action.meta.arg?.background) {
          if (!state.chosenMarket || String(state.chosenMarket.id) !== String(action.payload?.id)) {
            return state
          }
          if (state.marketDetailRequestId && state.marketDetailRequestId !== action.meta.requestId) {
            return state
          }
          const incoming = action.payload?.options || []
          const byId = new Map(
            (state.chosenMarket.options || []).map((option) => [String(option.id), option])
          )
          for (const option of incoming) {
            const prev = byId.get(String(option.id))
            byId.set(String(option.id), prev ? { ...prev, ...option } : option)
          }
          return {
            ...state,
            chosenMarket: {
              ...state.chosenMarket,
              ...action.payload,
              options: [...byId.values()],
              chosenOptionId: state.chosenMarket.chosenOptionId
            }
          }
        }

        if (state.marketDetailRequestId && state.marketDetailRequestId !== action.meta.requestId) {
          return state
        }
        const nextOptions = action.payload?.options || []
        const prevChosen = state.chosenMarket?.chosenOptionId
        const sameMarket =
          state.chosenMarket?.id != null &&
          String(state.chosenMarket.id) === String(action.payload?.id)
        const keepChosen =
          sameMarket &&
          prevChosen != null &&
          nextOptions.some((option) => String(option.id) === String(prevChosen))

        const clearOptionData = !keepChosen

        return {
          ...state,
          chosenMarket: {
            ...action.payload,
            chosenOptionId: keepChosen
              ? prevChosen
              : (nextOptions[0]?.id ?? null)
          },
          myLimitOrders: clearOptionData ? [] : state.myLimitOrders,
          myOptionPosition: clearOptionData ? null : state.myOptionPosition,
          myOptionOrders: clearOptionData ? {
            page: 1,
            rows: [],
            count: 0,
            state: ORDER_STATES.OPEN,
            orderType: ORDER_TYPES.LIMIT,
            side: ORDER_SIDES.BID
          } : state.myOptionOrders,
          marketDetailLoading: false,
          marketDetailRequestId: null
        }
      })
      .addCase(getMarketDetails.rejected, (state, action) => {
        if (action.meta.arg?.background) return state
        if (state.marketDetailRequestId && state.marketDetailRequestId !== action.meta.requestId) {
          return state
        }
        return {
          ...state,
          marketDetailLoading: false,
          marketDetailRequestId: null
        }
      })
    builder
      .addCase(getOptionDetails.fulfilled, (state, action) => {
        const optionId =
          action.meta.arg?.optionId ?? action.payload?.optionId
        return {
          ...state,
          chosenMarket: {
            ...state.chosenMarket,
            options: state.chosenMarket.options.map(option =>
              +option.id === +optionId
                ? { ...option, ...action.payload }
                : option
              )
            }
          }
        })
      .addCase(getOptionDetails.rejected, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, optionOrderbook: true }
        }
      })
    builder
      .addCase(getOptionGraphData.fulfilled, (state, action) => {
        const optionId = action.payload?.optionId ?? action.meta.arg?.optionId
        if (!optionId || !state.chosenMarket?.options) return state
        return {
          ...state,
          chosenMarket: {
            ...state.chosenMarket,
            options: state.chosenMarket.options.map(option =>
              +option.id === +optionId
                ? { ...option, optionDataPoints: action.payload.optionDataPoints }
                : option
            )
          }
        }
      })
    builder
      .addCase(getOptionOrderbook.pending, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, optionOrderbook: true }
        }
      })
      .addCase(getOptionOrderbook.fulfilled, (state, action) => {
        const optionId = action.meta.arg?.optionId
        const { orderbook, myLimitOrders, yesPrice } = action.payload ?? {}
        const patchOptionPrices = (options) => (options ?? []).map((option) =>
          yesPrice != null && optionId != null && +option.id === +optionId
            ? { ...option, yesPrice, noPrice: 100 - yesPrice }
            : option
        )

        return {
          ...state,
          componentLoader: { ...state.componentLoader, optionOrderbook: false },
          ...(myLimitOrders != null ? { myLimitOrders } : {}),
          chosenMarket: {
            ...state.chosenMarket,
            orderbook,
            ...(yesPrice != null && optionId != null && state.chosenMarket?.options ? {
              options: patchOptionPrices(state.chosenMarket.options)
            } : {})
          }
        }
      })
      .addCase(getOptionOrderbook.rejected, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, optionOrderbook: false }
        }
      })
    builder
      .addCase(getMyPositions.pending, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myPositions: true }
        }
      })
      .addCase(getMyPositions.fulfilled, (state, action) => {
        const isResolved = action.meta.arg?.state === USER_POSITION_STATES.RESOLVED
        
        
        const positions = action.payload.rows.map(r => {
          return {
            thumbnailUrl: r.thumbnailUrl,
            title: r.questionTitle,
            option: r.optionTitle,
            outcome: r.outcome,
            yesShares: (+r.yesShares).toFixed(2) + ` (${(+r.lockedYesShares).toFixed(2)} locked)`,
            noShares: (+r.noShares).toFixed(2) + ` (${(+r.lockedNoShares).toFixed(2)} locked)`,
            fee: '$' + getPrecision(r.takerFeeCharged || 0).toFixed(2),
            realizedPnl: '$' + getPrecision(r.realizedPnl).toFixed(2),
            avgYesPrice: (+r.avgYesPrice).toFixed(2),
            avgNoPrice: (+r.avgNoPrice).toFixed(2),
            questionId: r.questionId,
            optionId: r.optionId,
            id: r.id,
          }
        })

        return {
          ...state,
          myPositions: { rows: positions, count: action.payload.count },
          componentLoader: { ...state.componentLoader, myPositions: false }
        }
      })
      .addCase(getMyPositions.rejected, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myPositions: false }
        }
      })
    builder
      .addCase(getMyOrders.pending, (state, action) => {
        const isOptionScoped = action.meta.arg?.optionId != null
        return {
          ...state,
          componentLoader: {
            ...state.componentLoader,
            ...(isOptionScoped ? { myLimitOrders: true } : { myOrders: true })
          }
        }
      })
      .addCase(getMyOrders.fulfilled, (state, action) => {
        const isOptionScoped = action.meta.arg?.optionId != null

        if (isOptionScoped) {
          const orders = action.payload.rows ?? []
          return {
            ...state,
            myLimitOrders: orders,
            componentLoader: { ...state.componentLoader, myLimitOrders: false }
          }
        }

        const orders = action.payload.rows.map(mapPortfolioOrderRow)

        return {
          ...state,
          myOrders: { rows: orders, count: action.payload.count },
          componentLoader: { ...state.componentLoader, myOrders: false }
        }
      })
      .addCase(getMyOrders.rejected, (state, action) => {
        const isOptionScoped = action.meta.arg?.optionId != null
        return {
          ...state,
          componentLoader: {
            ...state.componentLoader,
            ...(isOptionScoped ? { myLimitOrders: false } : { myOrders: false })
          }
        }
      })
    builder
      .addCase(getMyOptionOrders.pending, (state, action) => {
        return {
          ...state,
          myOptionOrders: {
            rows: [],
            count: 0,
            state: action.meta.arg?.state ?? state.myOptionOrders.state,
            orderType: action.meta.arg?.orderType ?? state.myOptionOrders.orderType,
            side: action.meta.arg?.side ?? state.myOptionOrders.side
          },
          componentLoader: { ...state.componentLoader, myOptionOrders: true }
        }
      })
      .addCase(getMyOptionOrders.fulfilled, (state, action) => {
        const orders = action.payload.rows.map(mapOptionOrderRow)

        return {
          ...state,
          myOptionOrders: {
            rows: orders,
            count: action.payload.count,
            state: action.meta.arg?.state ?? state.myOptionOrders.state,
            orderType: action.meta.arg?.orderType ?? state.myOptionOrders.orderType,
            side: action.meta.arg?.side ?? state.myOptionOrders.side
          },
          componentLoader: { ...state.componentLoader, myOptionOrders: false }
        }
      })
      .addCase(getMyOptionOrders.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myOptionOrders: false }
        }
      })
    builder
      .addCase(cancelLimitOrder.fulfilled, (state, action) => {
        const removedOrder = action.payload?.order ?? action.payload?.removedOrder ?? action.payload?.cancelledOrder
        const removedOrderId = removedOrder?.id
        const removedOptionId = removedOrder?.optionId

        if (removedOrderId == null || removedOptionId == null) return

        const beforeCount = state.myOrders.rows.length
        state.myOrders.rows = state.myOrders.rows.filter(
          (row) => !(+row?.id === +removedOrderId && +row?.optionId === +removedOptionId)
        )
        const removedCount = beforeCount - state.myOrders.rows.length

        if (removedCount > 0) {
          state.myOrders.count = Math.max(0, (state.myOrders.count ?? 0) - removedCount)
        }

        const beforeOptionCount = state.myOptionOrders.rows.length
        state.myOptionOrders.rows = state.myOptionOrders.rows.filter(
          (row) => +row?.id !== +removedOrderId
        )
        const removedOptionCount = beforeOptionCount - state.myOptionOrders.rows.length

        if (removedOptionCount > 0) {
          state.myOptionOrders.count = Math.max(0, (state.myOptionOrders.count ?? 0) - removedOptionCount)
        }
      })
    builder
      .addCase(getActiveMarketOrder.fulfilled, (state, action) => {
        return {
          ...state,
          activeMarketOrder: action.payload
        }
      })
    builder
      .addCase(getHistory.pending, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, history: true }
        }
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        const rows = action.payload.rows.map(mapActivityLogRow)
        const notificationItems = state.notifications.items.length === 0
          ? (action.payload?.rows ?? [])
            .slice(0, 20)
            .map((row) => mapActivityToNotification(row, { read: true }))
            .filter(Boolean)
          : state.notifications.items

        return {
          ...state,
          history: { rows, count: action.payload.count },
          notifications: state.notifications.items.length === 0 && notificationItems.length > 0
            ? { ...state.notifications, items: notificationItems, unreadCount: 0 }
            : state.notifications,
          componentLoader: { ...state.componentLoader, history: false }
        }
      })
      .addCase(getHistory.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, history: false }
        }
      })
    builder
      .addCase(getMyProfit.pending, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, portfolio: true }
        }
      })
      .addCase(getMyProfit.fulfilled, (state, action) => {
        return {
          ...state,
          portfolioData: action.payload,
          componentLoader: { ...state.componentLoader, portfolio: false }
        }
      })
      .addCase(getMyProfit.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, portfolio: false }
        }
      })
    builder
      .addCase(getMarketActivity.pending, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, marketActivity: true }
        }
      })
      .addCase(getMarketActivity.fulfilled, (state, action) => {
        return {
          ...state,
          marketActivity: {
            rows: action.payload.rows.map(mapMarketActivityLogRow),
            count: action.payload.count
          },
          componentLoader: { ...state.componentLoader, marketActivity: false }
        }
      })
      .addCase(getMarketActivity.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, marketActivity: false }
        }
      })
    builder
      .addCase(getTopPositions.pending, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, topPositions: true }
        }
      })
      .addCase(getTopPositions.fulfilled, (state, action) => {
        return {
          ...state,
          topPositions: {
            rows: action.payload.rows.map(r => ({
              thumbnailUrl: r.thumbnailUrl,
              userName: r.userName,
              name: r.firstName + ' ' + r.lastName,
              yesShares: (+r.yesShares).toFixed(2),
              noShares: (+r.noShares).toFixed(2),
              realizedPnl: (+r.realizedPnl).toFixed(2),
              userId: r.userId
            })),
            count: action.payload.count
          },
          componentLoader: { ...state.componentLoader, topPositions: false }
        }
      })
      .addCase(getTopPositions.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, topPositions: false }
        }
      })
    builder
      .addCase(getTopHolders.pending, (state, action) => {
        const { outcome } = action.meta.arg || {}

        if (outcome === 'yes') {
          return {
            ...state,
            componentLoader: { ...state.componentLoader, topYesHolders: true }
          }
        }

        if (outcome === 'no') {
          return {
            ...state,
            componentLoader: { ...state.componentLoader, topNoHolders: true }
          }
        }

        return state
      })
      .addCase(getTopHolders.fulfilled, (state, action) => {
        const { outcome } = action.meta.arg || {}

        const topHolders = {
          rows: action.payload.rows.map(r => ({
            thumbnailUrl: r.thumbnailUrl,
            userName: r.userName,
            name: r.firstName + ' ' + r.lastName,
            shares: r.shares,
            userId: r.userId,
          })),
          count: action.payload.count
        }

        if (outcome === 'yes') {
          return {
            ...state,
            topYesHolders: topHolders,
            componentLoader: { ...state.componentLoader, topYesHolders: false }
          }
        }
        
        if (outcome === 'no') {
          return {
            ...state,
            topNoHolders: topHolders,
            componentLoader: { ...state.componentLoader, topNoHolders: false }
          }
        }
      })
      .addCase(getTopHolders.rejected, (state, action) => {
        const { outcome } = action.meta.arg || {}

        return {
          ...state,
          componentLoader: {
            ...state.componentLoader,
            ...(outcome === 'yes' ? { topYesHolders: false } : {}),
            ...(outcome === 'no' ? { topNoHolders: false } : {})
          }
        }
      })
    builder
      .addCase(getMarketComments.pending, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, marketComments: true }
        }
      })
      .addCase(getMarketComments.fulfilled, (state, action) => {
        const { loadMore, parentCommentId } = action.meta.arg || {}

        if (parentCommentId) {
          return {
            ...state,
            marketComments: {
              ...state.marketComments,
              rows: state.marketComments.rows.map(comment => {
                if (+comment.id !== +parentCommentId) return comment

                return {
                  ...comment,
                  replies: loadMore
                    ? [...(comment.replies || []), ...action.payload.rows]
                    : action.payload.rows,
                  repliesCount: action.payload.count
                }
              })
            },
            componentLoader: { ...state.componentLoader, marketComments: false }
          }
        }

        return {
          ...state,
          marketComments: {
            count: action.payload.count,
            rows: loadMore ? [...state.marketComments.rows, ...action.payload.rows] : action.payload.rows
          },
          componentLoader: { ...state.componentLoader, marketComments: false }
        }
      })
      .addCase(getMarketComments.rejected, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, marketComments: false }
        }
      })
    builder
      .addCase(getMyMarketComments.pending, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myMarketComments: true }
        }
      })
      .addCase(getMyMarketComments.fulfilled, (state, action) => {
        return {
          ...state,
          myMarketComments: action.payload,
          componentLoader: { ...state.componentLoader, myMarketComments: false }
        }
      })
      .addCase(getMyMarketComments.rejected, (state, action) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myMarketComments: false }
        }
      })
    builder
      .addCase(likeToggle.fulfilled, (state, action) => {
        const { commentId, likes, liked } = action.payload || {}
        if (commentId == null) return state

        return {
          ...state,
          marketComments: {
            ...state.marketComments,
            rows: state.marketComments.rows.map(comment => {
              if (+comment.id === +commentId) {
                return { ...comment, likes, likedByMe: liked }
              }

              if (!comment.replies?.length) return comment

              return {
                ...comment,
                replies: comment.replies.map(reply =>
                  +reply.id === +commentId
                    ? { ...reply, likes, likedByMe: liked }
                    : reply
                )
              }
            })
          }
        }
      })
    builder
      .addCase(getMyOptionPosition.pending, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myOptionPosition: true }
        }
      })
      .addCase(getMyOptionPosition.fulfilled, (state, action) => {
        return {
          ...state,
          myOptionPosition: action.payload.userPosition ?? null,
          componentLoader: { ...state.componentLoader, myOptionPosition: false }
        }
      })
      .addCase(getMyOptionPosition.rejected, (state) => {
        return {
          ...state,
          componentLoader: { ...state.componentLoader, myOptionPosition: false }
        }
      })
    // builder
    //   .addCase(getTopHolders.pending, (state, action) => {
    //     return {
    //       ...state,
    //       componentLoader: { ...state.componentLoader, topHolders: true }
    //     }
    //   })
    //   .addCase(getTopHolders.fulfilled, (state, action) => {
    //     return {
    //       ...state,
    //       topHolders: action.payload,
    //       componentLoader: { ...state.componentLoader, topHolders: false }
    //     }
    //   })
    //   .addCase(getTopHolders.rejected, (state, action) => {
    //     return {
    //       ...state,
    //       componentLoader: { ...state.componentLoader, topHolders: false }
    //     }
    //   })
    // builder
    //   .addCase(addComment.fulfilled, (state, action) => {
    //     return {
    //       ...state,
    //       marketComments: {
    //         rows: [...state.marketComments.rows],
    //         count: state.marketComments.count + 1
    //       }
    //     }
    //   })
    // builder
    //   .addCase(deleteComment.fulfilled, (state, action) => {
    //     return {
    //       ...state,
    //       marketComments: {
    //         rows: [...state.marketComments.rows],
    //         count: state.marketComments.count - 1
    //       }
    //     }
    //   })
    builder
      .addCase(fetchUserInformation.fulfilled, (state, action) => {
        return {
          ...state,
          favouriteQuestionIds: action.payload?.favouriteQuestions ?? []
        }
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        return {
          ...state,
          favouriteQuestionIds: action.payload?.user?.favouriteQuestions ?? []
        }
      })
      .addCase(toggleFavouriteQuestion.fulfilled, (state, action) => {
        return {
          ...state,
          favouriteQuestionIds: action.payload?.favouriteQuestionIds ?? state.favouriteQuestionIds
        }
      })
    builder
      .addCase(loadNotifications.pending, (state) => {
        return {
          ...state,
          notifications: {
            ...state.notifications,
            loading: true
          }
        }
      })
      .addCase(loadNotifications.fulfilled, (state, action) => {
        if (action.payload?.skipped || state.notifications.items.length > 0 || state.history.rows.length > 0) {
          return {
            ...state,
            notifications: {
              ...state.notifications,
              loading: false
            }
          }
        }

        return {
          ...state,
          notifications: {
            ...state.notifications,
            loading: false,
            items: (action.payload?.rows ?? [])
              .map((row) => mapActivityToNotification(row, { read: true }))
              .filter(Boolean),
            unreadCount: 0
          }
        }
      })
      .addCase(loadNotifications.rejected, (state) => {
        return {
          ...state,
          notifications: {
            ...state.notifications,
            loading: false
          }
        }
      })
      .addCase(getNews.pending, (state) => {
        return { ...state, newsInfo: { ...state.newsInfo, loading: true } }
      })
      .addCase(getNews.fulfilled, (state, action) => {
        const { articles, category, topCount } = action.payload || {}
        const mapped = (articles || []).map((a, i) => ({
          id: a.id || `${category}-${i}`,
          headline: a.headline,
          description: a.description,
          source: a.source,
          age: a.age || ''
        }))
        return {
          ...state,
          newsInfo: {
            ...state.newsInfo,
            loading: false,
            // Backend: top 2 per query (≤10); remaining → "More relevant news"
            topCount: typeof topCount === 'number' ? topCount : state.newsInfo.topCount,
            byCategory: {
              ...state.newsInfo.byCategory,
              [category]: mapped
            }
          }
        }
      })
      .addCase(getNews.rejected, (state) => {
        return { ...state, newsInfo: { ...state.newsInfo, loading: false } }
      })
  }
})

export const {
  setActiveCategory,
  toggleNewsPanel,
  closeNewsPanel,
  setChosenMarket,
  setIsDemoMode,
  setDemoWallet,
  demoOrdersReset,
  demoOrdersAdd,
  demoOrdersRemove,
  demoSyncBook,
  demoMarketClosed,
  demoMarketSettled,
  setChosenOptionId,
  commentAdded,
  commentDeleted,
  userPositionUpdated,
  userActivityReceived,
  marketActivityReceived,
  marketOptionReceived,
  toggleNotificationsPanel,
  closeNotificationsPanel,
  markAllNotificationsRead,
  walletBalanceNotificationReceived
} = predictionMarketSlice.actions

export default predictionMarketSlice.reducer
